import React, { memo, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { selectHydrationFinished } from 'app/hooks/reduxCreateSelectorHooks';
import useVideoStartTracking from 'app/components/tv/trackVideoSeeking';
import { LOADING_MESSAGE, LoadingIndicator } from '@fupa/fupa-uikit';
import { updateCurrentVideo } from 'app/routes/tv/TvPlaylistPageActions';
import { LoadingOverlay } from 'app/components/tv/LoadingOverlay';
import { captureSentryExceptionAndThrowLocal } from 'app/services/sentry/sentryLogging';
import { useWatchedPlaylistTracking } from 'app/hooks/useWatchedPlaylistTracking';
import CustomDomEvents from 'app/types/events/CustomDomEvents';
import useAdCredits from 'app/components/tv/helpers/useAdCredits';
import { setAdCreditsToSessionStorage } from 'app/helpers/adStepper';
const VideoJS = /*#__PURE__*/React.lazy(() => import('app/components/tv/VideoJS'));

// scrollable wrapper necessary to hide address-bar in mobile Browsers
// for chrome ios it's not enough to set 101vh to hide address-bar
// -> therefore it's set to 200vh to hopefully support all browsers
const Wrapper = styled.div.withConfig({
  componentId: "sc-yyvp9x-0"
})(["position:relative;width:100vw;height:200vh;background-color:black;padding-top:env(safe-area-inset-top);"]);

// Player-Container is fixed with height 100%
// to always have the height of the visible screen on mobile browsers,
// no matter if address-bar is shown or not
const VideoPlayerContainer = styled.div.withConfig({
  componentId: "sc-yyvp9x-1"
})(["position:fixed;top:0;left:0;width:100%;height:100%;overflow:hidden;"]);
const VideoPlayer = /*#__PURE__*/memo(function VideoPlayer(_ref) {
  let {
    video,
    playlist,
    isFetching,
    dispatch,
    ssrLoaded
  } = _ref;
  const currentVideoRef = useRef(null);
  const playerRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const history = useHistory();
  const {
    matchPlaylistSlug
  } = useParams();
  const hydrationFinished = useSelector(selectHydrationFinished);
  const handlePlayerReady = useCallback(player => {
    playerRef.current = player;
    setPlayerReady(true);
    player.on('ended', () => {
      window.dispatchEvent(new CustomEvent(CustomDomEvents.VideoEnded, {
        detail: {
          video: currentVideoRef.current,
          played: player.played()
        }
      }));
    });
    player.on('error', () => {
      const error = player.error();
      if (!error) {
        return;
      }
      if (error.code === error.MEDIA_ERR_NETWORK) {
        setIsNetworkError(true);
      }
      logVjsError(error, player.currentSrc());
      player.play();
    });
  }, []);
  useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player && currentVideoRef.current) {
        // update credits when site is left before video ended
        window.dispatchEvent(new CustomEvent(CustomDomEvents.PlayerLeft, {
          detail: {
            video: currentVideoRef.current,
            played: player.played()
          }
        }));
      }
    };
  }, [playerReady]);
  const adCreditsRef = useAdCredits(video);

  // Persist adCredits to sessionStorage when the player (component) unmounts
  useEffect(() => {
    return () => {
      try {
        setAdCreditsToSessionStorage(adCreditsRef.current);
      } catch (e) {
        captureSentryExceptionAndThrowLocal(e);
      }
    };
  }, [adCreditsRef]);
  useEffect(() => {
    if (!video) {
      return;
    }
    currentVideoRef.current = video;
  }, [video]);
  useVideoStartTracking(playerRef, video, playerReady, playlist);
  useWatchedPlaylistTracking(playlist, video, playerRef, playerReady);
  const setCurrentVideo = React.useCallback(newCurrentVideo => {
    dispatch(updateCurrentVideo(newCurrentVideo));
    history.replace("/tv/match/".concat(matchPlaylistSlug, "/").concat(newCurrentVideo.slug));
  }, [matchPlaylistSlug, history, dispatch]);
  if (!video || !playlist) {
    if (isFetching) {
      return /*#__PURE__*/React.createElement(LoadingIndicator, {
        message: LOADING_MESSAGE.loadingData,
        bgColor: false
      });
    } else {
      return null;
    }
  }
  const Player = hydrationFinished ? /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(LoadingOverlay, {
      show: true
    })
  }, /*#__PURE__*/React.createElement(VideoJS, {
    onReady: handlePlayerReady,
    video: video,
    playlist: playlist,
    setCurrentVideo: setCurrentVideo,
    playerReady: playerReady,
    isNetworkError: isNetworkError,
    setIsNetworkError: setIsNetworkError,
    isFirstPage: ssrLoaded,
    adCreditsRef: adCreditsRef
  })) : /*#__PURE__*/React.createElement(LoadingOverlay, {
    show: true
  });
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(VideoPlayerContainer, null, Player));
});
const logVjsError = (error, src) => {
  if (error.code === error.MEDIA_ERR_SRC_NOT_SUPPORTED) {
    captureSentryExceptionAndThrowLocal(new Error('MediaError: Source is not supported'), {
      extra: {
        type: 'vjs error',
        errorInfo: error,
        src
      }
    });
  } else {
    captureSentryExceptionAndThrowLocal(new Error("VideoJS error-handler: ".concat(error.message)), {
      extra: {
        errorInfo: {
          type: 'vjs error',
          code: error.code,
          message: error.message
        },
        src
      }
    });
  }
};
export default VideoPlayer;